import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { toast } from "react-toastify";
import { LUXURY } from "data/guestLuxuryTheme";

const STEPS = [
  "Open your phone Settings and go to Wi-Fi.",
  "Turn Wi-Fi on and wait for available networks.",
  "Tap the network name shown below.",
  "Enter the password and join — you are all set.",
];

const CopyRow = ({ label, value, onCopy }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      p: 1.35,
      borderRadius: "14px",
      bgcolor: LUXURY.cardSoft,
      border: `1px solid ${LUXURY.cardBorder}`,
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: LUXURY.hint,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.95rem",
          color: LUXURY.ink,
          wordBreak: "break-word",
          lineHeight: 1.3,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
    <Button
      onClick={onCopy}
      disabled={!value}
      sx={{
        minWidth: 40,
        minHeight: 40,
        p: 0,
        borderRadius: "12px",
        color: LUXURY.gold,
        bgcolor: "rgba(201,166,107,0.12)",
        "&:hover": { bgcolor: "rgba(201,166,107,0.2)" },
      }}
      aria-label={`Copy ${label}`}
    >
      <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
    </Button>
  </Box>
);

const WifiSheet = ({ open, onClose, wifiName, wifiPassword }) => {
  const copy = async (text, label) => {
    if (!text) {
      toast.info("Wi-Fi details are not available yet");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.info(text);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "22px",
          mx: 2,
          mb: 2,
          bgcolor: LUXURY.card,
          border: `1px solid ${LUXURY.cardBorder}`,
          maxWidth: 400,
        },
      }}
    >
      <DialogContent sx={{ pt: 3, px: 2.5, pb: 1 }}>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              mx: "auto",
              mb: 1.25,
              borderRadius: "16px",
              bgcolor: "rgba(201,166,107,0.14)",
              color: LUXURY.gold,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WifiRoundedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: LUXURY.displayFont,
              fontWeight: 600,
              fontSize: "1.35rem",
              color: LUXURY.ink,
              lineHeight: 1.2,
            }}
          >
            Connect to Wi-Fi
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: "0.84rem",
              color: LUXURY.muted,
              lineHeight: 1.45,
            }}
          >
            Free guest Wi-Fi for your stay. Follow the steps below.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <CopyRow
            label="Network name"
            value={wifiName}
            onCopy={() => copy(wifiName, "Network name")}
          />
          <CopyRow
            label="Password"
            value={wifiPassword}
            onCopy={() => copy(wifiPassword, "Password")}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: LUXURY.gold,
              mb: 1,
            }}
          >
            How to connect
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {STEPS.map((step, index) => (
              <Box key={step} sx={{ display: "flex", alignItems: "flex-start", gap: 1.1 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    flexShrink: 0,
                    borderRadius: "50%",
                    bgcolor: "rgba(201,166,107,0.18)",
                    color: LUXURY.gold,
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 0.1,
                  }}
                >
                  {index + 1}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.84rem",
                    color: LUXURY.muted,
                    lineHeight: 1.45,
                    pt: 0.15,
                  }}
                >
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography
          sx={{
            mt: 1.75,
            fontSize: "0.75rem",
            color: LUXURY.hint,
            lineHeight: 1.45,
            textAlign: "center",
          }}
        >
          Need help? Ask the front desk — they can assist with connection.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0.5, gap: 1, flexDirection: "column" }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => copy(wifiPassword, "Password")}
          sx={{
            minHeight: 46,
            fontWeight: 800,
            background: LUXURY.ctaGradient,
            color: LUXURY.forestDeep,
            borderRadius: "14px",
            boxShadow: LUXURY.ctaShadow,
            "&:hover": { background: LUXURY.ctaGradientHover },
          }}
        >
          Copy password
        </Button>
        <Button
          fullWidth
          onClick={onClose}
          sx={{ minHeight: 44, fontWeight: 700, color: LUXURY.ink }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WifiSheet;
